/* eslint-disable indent */
import { useEffect, useState } from "react";
import {
  Edit,
  ArrayInput,
  SelectInput,
  ReferenceInput,
  useRedirect,
  TabbedForm,
  FormTab,
  ReferenceManyField,
  SimpleFormIterator,
  Datagrid,
  TextField,
  SelectField,
  FunctionField,
  TopToolbar,
  useDataProvider,
  useNotify,
  required,
  SimpleForm,
  Toolbar,
  SaveButton,
  maxLength,
  useListContext,
  useRecordContext,
} from "react-admin";
import { Link, useParams } from "react-router-dom";
import isEqual from "lodash/isEqual";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";

import AddContentItemButton from "./AddContentItemButton";
import ContentPublicationButton from "./ContentPublicationButton";
import ContentItemShowButton from "./ContentItemShowButton";
import { ContentItemEdit } from "../ContentItem/ContentItemCreate";
import { ContentTagEdit } from "../ContentTag/ContentTagCreate";
import ContentItemOrderableList from "../ContentItem/ContentItemOrderableList";
import resources from "../../constants/resources";
import {
  CONTENT_ITEMS_TAB,
  CONTENT_ITEMS_ORDER_TAB,
  CONTENT_TAGS_TAB,
  CONTENT_EDIT_TITLE,
  ADD_CONTENT_TAG_LABEL,
} from "../../constants/titleConstants";
import {
  CONTENT_ITEM_TYPE_RENDERER,
  CONTENT_TYPES_LIST,
  CONTENT_TYPES,
  ALL_CONTENT_ITEM_TYPE_LIST,
  SURVEY_CONTENT_ITEM_TYPES_BY_ID,
  QUIZ_TUTORIAL_CONTENT_ITEM_TYPES_BY_ID,
  CONTENT_DIFFICULTY,
  SITES,
  LANGUAGES,
  KUDZU_SITES,
} from "../../constants/contentConstants";
import { PP_ADMIN_REFERRER, PP_ADMIN_TOKEN } from "../../constants/storageKeys";
import { formatImage, getNonNullishValues } from "../../utils/InputUtils";
import {
  getDropDownSiteOptions,
  openWindowWithExternalLink,
} from "../../utils/uriUtils";
import { CONTENT_HELPER_TEXT } from "../../constants/helperTextConstants";
import {
  AddAndOrderButtonStyles,
  AutoCompleteInputField,
  ImageInputField,
  NumberInputField,
  SelectInputField,
  TextInputField,
} from "../../styles/commonStyles";
import { useOnError } from "../../utils/errorUtils";
import PreviewImage from "../common/PreviewImage";

const { REACT_APP_AUTHED_PP_URL, REACT_APP_KD_PP_URL } = process.env;

const PreviewToolbar = props => {
  const { pristine } = props;
  return (
    <Toolbar {...props}>
      <SaveButton
        label="Preview"
        disabled={pristine}
        icon={<VisibilityIcon />}
      />
    </Toolbar>
  );
};

const ContentEditActions = ({
  duplicateContentClickHandler,
  previewContentClickHandler,
  isLoadingDuplication,
  handlePreviewCloseClick,
  showDialog,
  dialogPreviewClickHandler,
}) => {
  /* TODO: KD-169 */
  const isKudzuSite =
    localStorage.getItem(PP_ADMIN_REFERRER) === KUDZU_SITES[0].title;
  return (
    <TopToolbar>
      <Button color="primary" onClick={duplicateContentClickHandler}>
        {isLoadingDuplication ? <CircularProgress /> : <FileCopyIcon />}
        Duplicate Content
      </Button>
      <Button color="primary" onClick={previewContentClickHandler}>
        <VisibilityIcon />
        Preview Content
      </Button>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={handlePreviewCloseClick}
        aria-label="Preview Content"
      >
        <DialogTitle> Preview Content </DialogTitle>
        <SimpleForm
          onSubmit={dialogPreviewClickHandler}
          toolbar={<PreviewToolbar />}
        >
          <SelectInput
            source="site_type"
            choices={getDropDownSiteOptions(isKudzuSite ? KUDZU_SITES : SITES)}
            defaultValue={isKudzuSite ? KUDZU_SITES[0].param : SITES[4].param}
            validate={required()}
          />
          <SelectInput source="language" choices={LANGUAGES} />
        </SimpleForm>
      </Dialog>
    </TopToolbar>
  );
};

const ContentEdit = () => {
  const [contentItemsTotal, setContentItemsTotal] = useState(0);
  const [contentTypeQueryParam, setContentTypeQueryParam] = useState("");
  const [currentContentItemTypes, setCurrentContentItemTypes] = useState([]);
  const [isLoadingDuplication, setIsLoadingDuplication] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const { id } = useParams();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const onError = useOnError(notify);

  const handlePreviewClick = () => {
    setShowDialog(true);
  };

  const handlePreviewCloseClick = () => {
    setShowDialog(false);
  };

  const REACT_APP_PP_BASE_URL =
    localStorage.getItem(PP_ADMIN_REFERRER) === KUDZU_SITES[0].title
      ? REACT_APP_KD_PP_URL
      : REACT_APP_AUTHED_PP_URL;

  const contentTypeValidator = value => {
    const allowedContentTypes = [];
    const isCurrentContentItemsSubsetOfQuizAndTutorial =
      currentContentItemTypes.length === 0 ||
      currentContentItemTypes.every(contentItemId =>
        QUIZ_TUTORIAL_CONTENT_ITEM_TYPES_BY_ID.includes(contentItemId)
      );
    if (isCurrentContentItemsSubsetOfQuizAndTutorial) {
      allowedContentTypes.push(
        CONTENT_TYPES.quiz.id,
        CONTENT_TYPES.tutorial.id
      );
    }
    const isCurrentContentItemsSubsetOfSurvey =
      currentContentItemTypes === 0 ||
      currentContentItemTypes.every(contentItemId =>
        SURVEY_CONTENT_ITEM_TYPES_BY_ID.includes(contentItemId)
      );
    if (isCurrentContentItemsSubsetOfSurvey) {
      allowedContentTypes.push(CONTENT_TYPES.survey.id);
    }

    if (!allowedContentTypes.includes(value)) {
      return `${value.toUpperCase()} is not allowed as this content
			contains content-items not supported by this content-type`;
    }
    return undefined;
  };

  const duplicateContentClickHandler = () => {
    setIsLoadingDuplication(true);
    dataProvider
      .create(resources.CONTENTS_DUPLICATION.url, {
        data: { id },
      })
      .then(({ data }) => {
        setIsLoadingDuplication(false);
        notify("Duplicate Content created successfully", { type: "success" });
        redirect(`/${resources.CONTENT.url}/${data.id}`);
      })
      .catch(error => {
        setIsLoadingDuplication(false);
        notify(error, { type: "error" });
      });
  };

  const previewContentClickHandler = values => {
    const token = localStorage.getItem(PP_ADMIN_TOKEN);
    // eslint-disable-next-line max-len
    const authedFEContentLink = `${REACT_APP_PP_BASE_URL}/${values.language}/${
      values.site_type
    }/website/content/${id}?jwt=${token}&preview=${1}`;

    openWindowWithExternalLink(authedFEContentLink);
  };

  return (
    <Edit
      title={CONTENT_EDIT_TITLE}
      actions={
        <ContentEditActions
          duplicateContentClickHandler={duplicateContentClickHandler}
          isLoadingDuplication={isLoadingDuplication}
          previewContentClickHandler={handlePreviewClick}
          handlePreviewCloseClick={handlePreviewCloseClick}
          showDialog={showDialog}
          dialogPreviewClickHandler={previewContentClickHandler}
        />
      }
      mutationMode="pessimistic"
      transform={getNonNullishValues}
      mutationOptions={{ onError }}
    >
      <TabbedForm>
        <FormTab label="Summary">
          <TextInputField InputProps={{ disabled: true }} source="id" />
          {/* TODO: KD-169 */}
          <ArrayInput
            source="sites"
            validate={required()}
            helperText={CONTENT_HELPER_TEXT.sites}
          >
            <SimpleFormIterator>
              <SelectInputField
                source="id"
                choices={
                  localStorage.getItem(PP_ADMIN_REFERRER) ===
                  KUDZU_SITES[0].title
                    ? KUDZU_SITES
                    : SITES
                }
                validate={required()}
                label="Site"
              />
            </SimpleFormIterator>
          </ArrayInput>
          <ReferenceInput
            label={resources.TOPIC.label}
            source="topic"
            perPage={800}
            reference={resources.TOPIC.url}
          >
            <AutoCompleteInputField
              optionText="name_en"
              helperText={CONTENT_HELPER_TEXT.topic}
              validate={required()}
            />
          </ReferenceInput>
          <SelectInputField
            source="content_type"
            choices={CONTENT_TYPES_LIST}
            validate={[contentTypeValidator, required()]}
            helperText={CONTENT_HELPER_TEXT.contentType}
          />
          <SelectInputField
            source="difficulty"
            choices={CONTENT_DIFFICULTY}
            helperText={CONTENT_HELPER_TEXT.difficulty}
          />
          <NumberInputField
            source="featured_order"
            min="0"
            label="Feature Order"
            helperText={CONTENT_HELPER_TEXT.featureOrder}
          />
          <TextInputField
            source="name_en"
            validate={[required(), maxLength(100)]}
            helperText={CONTENT_HELPER_TEXT.nameEn}
          />
          <TextInputField
            source="name_es"
            validate={maxLength(100)}
            helperText={CONTENT_HELPER_TEXT.nameEs}
          />
          <TextInputField
            multiline
            source="description_en"
            validate={required()}
            helperText={CONTENT_HELPER_TEXT.descriptionEn}
          />
          <TextInputField
            multiline
            source="description_es"
            helperText={CONTENT_HELPER_TEXT.descriptionEs}
          />
          <ImageInputField
            source="cover_image_url"
            format={formatImage}
            label="Cover Image"
            accept="image/*"
            placeholder={<p>Drop your file here</p>}
            validate={required()}
            helperText={CONTENT_HELPER_TEXT.coverImage}
          >
            <PreviewImage source="src" />
          </ImageInputField>
          <TextInputField
            multiline
            source="admin_notes"
            helperText={CONTENT_HELPER_TEXT.adminNotes}
          />
          <NumberInputField
            source="base_point_value"
            min="0"
            validate={required()}
            helperText={CONTENT_HELPER_TEXT.basePointValue}
          />
          <ContentPublicationButton />
        </FormTab>
        <FormTab
          label={CONTENT_ITEMS_TAB}
          path="content_items"
          sx={{ "& > div": { width: "100%" } }}
        >
          <ReferenceManyField
            addLabel={false}
            reference={resources.CONTENT_ITEMS.url}
            target="content"
            sort={{ field: "order", order: "ASC" }}
          >
            <ContentItemsDatagrid
              currentContentItemsTotal={contentItemsTotal}
              setContentItemsTotal={setContentItemsTotal}
              contentTypeQueryParam={contentTypeQueryParam}
              currentContentItemTypes={currentContentItemTypes}
              setCurrentContentItemTypes={setCurrentContentItemTypes}
            />
          </ReferenceManyField>
          <AddContentItemButton
            nextOrder={contentItemsTotal + 1}
            currentContentTypeQueryParam={contentTypeQueryParam}
            setContentTypeQueryParam={setContentTypeQueryParam}
          />
        </FormTab>
        <FormTab label={CONTENT_ITEMS_ORDER_TAB} path="content_items_orderable">
          <ContentItemOrderableList />
        </FormTab>
        <FormTab
          label={CONTENT_TAGS_TAB}
          path="content_tags"
          sx={{ "& > div": { width: "100%" } }}
        >
          <ReferenceManyField
            addLabel={false}
            reference={resources.CONTENT_TAGS.url}
            target="content"
            sort={{ field: "id", order: "DESC" }}
          >
            <Datagrid
              bulkActionButtons={false}
              expand={<ContentTagEdit isExpandMode contentId={id} />}
            >
              <TextField source="id" />
              <TextField source="key" sortable={false} />
              <TextField source="value" sortable={false} />
            </Datagrid>
          </ReferenceManyField>
          <AddAndOrderButtonStyles
            variant="text"
            component={Link}
            to={`/${resources.CONTENT_TAGS.url}/create?content_id=${id}`}
            title={ADD_CONTENT_TAG_LABEL}
            startIcon={<ChatBubbleIcon />}
          >
            {ADD_CONTENT_TAG_LABEL}
          </AddAndOrderButtonStyles>
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};

const ContentItemsDatagrid = props => {
  const {
    contentTypeQueryParam,
    currentContentItemsTotal,
    setContentItemsTotal,
    currentContentItemTypes,
    setCurrentContentItemTypes,
  } = props;
  const content = useRecordContext();
  const { data = [], resource, total } = useListContext();
  const contentItemTypes = Object.keys(data).map(
    id => data[id].content_item_type
  );

  useEffect(() => {
    if (!isEqual(contentItemTypes, currentContentItemTypes)) {
      setCurrentContentItemTypes(contentItemTypes);
    }

    if (total !== currentContentItemsTotal) {
      setContentItemsTotal(total);
    }
  }, [contentItemTypes, total]);

  return (
    <Datagrid
      bulkActionButtons={false}
      expand={
        <ContentItemEdit
          isExpandMode
          contentTypeQueryParam={contentTypeQueryParam}
        />
      }
    >
      <TextField source="id" />
      <SelectField
        source="content_item_type"
        choices={ALL_CONTENT_ITEM_TYPE_LIST}
      />
      <FunctionField
        label="Preview"
        render={record =>
          CONTENT_ITEM_TYPE_RENDERER[record.content_item_type]
            ? CONTENT_ITEM_TYPE_RENDERER[record.content_item_type].previewField(
                { record }
              )
            : null
        }
      />
      <ContentItemShowButton
        contentTypeQueryParam={contentTypeQueryParam}
        contentId={content.id}
        resource={resource}
      />
    </Datagrid>
  );
};

export default ContentEdit;
